import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { GiCrossedBones } from 'react-icons/gi';
import { LuSave } from 'react-icons/lu';
import { toast } from "react-toastify";
import { useGetBranchByIdQuery, useSaveBranchMutation } from "../../../../services/redux/api/components";

const AddBranch = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);   // Determine if in edit mode based on presence of id

  const [saveBranch] = useSaveBranchMutation();
  const { data: branchData, isLoading } = useGetBranchByIdQuery(id, { skip: !isEditMode });
  
  const initialValues = {
    branchName: branchData?.data?.Branch_Name || "",
    branchShortName: branchData?.data?.Branch_ShortName || "",
    managerName: branchData?.data?.Manager_Name || "",
    managerContactNo: branchData?.data?.Manager_Contact_No || "",
    addressLine1: branchData?.data?.Address_1 || "",
    addressLine2: branchData?.data?.Address_2 || "",
    addressLine3: branchData?.data?.Address_3 || "",
    city: branchData?.data?.City || "",
    state: branchData?.data?.State || "",
    pincode: branchData?.data?.PinCode || "",
  };

  const validationSchema = Yup.object().shape({
    branchName: Yup.string().required("Branch Name is required"),
    managerName: Yup.string().required("Manager Name is required"),
    managerContactNo: Yup.string()
      .required("Manager Contact Number is required")
      .matches(/^[0-9]{10}$/, "Contact number must be 10 digits"),
    addressLine1: Yup.string().required("Address Line 1 is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
  });

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      ...values,
      mode: isEditMode ? "Update" : "Add",
    }
    if (isEditMode) {
      payload.BranchId = id;
    }
    await saveBranch(payload)
      .then((response) => {
        console.log("API Response:", response); // Check the response from the API
        if (response?.data?.success) {
          toast.success(response.data.message || (isEditMode ? "Branch Updated Successfully" : "Branch added successfully!"));
          resetForm();
          window.history.back();
        } else {
          toast.error(response?.data?.message || "Failed to add branch. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error during API call:", error);
        toast.error("An error occurred while adding the branch. Please try again.");
      });

  };

  if (isEditMode && isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="formContainer lg:p-4">
      <div className="flex flex-col p-4 lg:mx-5 border rounded bg-white" style={{ boxShadow: "0px 0px 12px #cac8c8" }}>
        <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">Branch Master</h5>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form id="branchForm">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Branch Name  <span className="text-danger">*</span></label>
                  <Field type="text" name="branchName" className="form-control custom-shadow" />
                  <ErrorMessage name="branchName" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Branch Short Name</label>
                  <Field type="text" name="branchShortName" className="form-control custom-shadow" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Manager Name  <span className="text-danger">*</span></label>
                  <Field type="text" name="managerName" className="form-control custom-shadow" />
                  <ErrorMessage name="managerName" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Manager Contact Number  <span className="text-danger">*</span></label>
                  <Field type="text" name="managerContactNo" className="form-control custom-shadow" />
                  <ErrorMessage name="managerContactNo" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-12">
                  <label className="form-label">Address Line 1  <span className="text-danger">*</span></label>
                  <Field type="text" name="addressLine1" className="form-control custom-shadow" />
                  <ErrorMessage name="addressLine1" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Address Line 2</label>
                  <Field type="text" name="addressLine2" className="form-control custom-shadow" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Address Line 3</label>
                  <Field type="text" name="addressLine3" className="form-control custom-shadow" />
                </div>

                <div className="col-md-4">
                  <label className="form-label">City  <span className="text-danger">*</span></label>
                  <Field type="text" name="city" className="form-control custom-shadow" />
                  <ErrorMessage name="city" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-4">
                  <label className="form-label">State  <span className="text-danger">*</span></label>
                  <Field type="text" name="state" className="form-control custom-shadow" />
                  <ErrorMessage name="state" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Pincode  <span className="text-danger">*</span></label>
                  <Field type="text" name="pincode" className="form-control custom-shadow" />
                  <ErrorMessage name="pincode" component="div" className="text-brandRed" />
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-5">
                <button type="submit" className="saveButton d-flex align-items-center px-4" disabled={isSubmitting}>
                  <LuSave className="me-2" />
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
                <button type="button" className="backButton d-flex align-items-center px-4" onClick={() => window.history.back()}>
                  <GiCrossedBones className="me-2" />
                  Cancel
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddBranch;